import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  searchDispatchCompanies,
  getDispatchCompany,
  addDispatchCompany,
  updateDispatchCompany,
  searchCargoCompanyNames,
  getCargoCompany,
} from "../services/companyService";
import { useCompanyStore } from "../stores/companyStore";
import type {
  CompanySearchRequest,
  AddDispatchCompanyRequest,
  UpdateDispatchCompanyRequest,
} from "../types/company.types";

export const useCompanyList = (params: CompanySearchRequest) =>
  useQuery({
    queryKey: ["dispatchCompanies", params],
    queryFn: () => searchDispatchCompanies(params),
    staleTime: 1000,
  });

export const useCompanyDetail = (companyGuid: string | null) =>
  useQuery({
    queryKey: ["dispatchCompany", companyGuid],
    queryFn: () => getDispatchCompany(companyGuid!),
    enabled: !!companyGuid,
    gcTime: 0,
  });

export const useAddCompany = () => {
  const qc = useQueryClient();
  const closeDrawer = useCompanyStore((s) => s.closeDrawer);

  return useMutation({
    mutationFn: (req: AddDispatchCompanyRequest) => addDispatchCompany(req),
    onSuccess: () => {
      toast.success("업체가 등록되었습니다.");
      qc.invalidateQueries({ queryKey: ["dispatchCompanies"] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`등록 실패: ${err.message}`);
    },
  });
};

export const useUpdateCompany = () => {
  const qc = useQueryClient();
  const closeDrawer = useCompanyStore((s) => s.closeDrawer);

  return useMutation({
    mutationFn: (req: UpdateDispatchCompanyRequest) =>
      updateDispatchCompany(req),
    onSuccess: () => {
      toast.success("업체 정보가 수정되었습니다.");
      qc.invalidateQueries({ queryKey: ["dispatchCompanies"] });
      closeDrawer();
    },
    onError: (err: Error) => {
      toast.error(`수정 실패: ${err.message}`);
    },
  });
};

export const useCargoCompanyNames = (searchStr: string) =>
  useQuery({
    queryKey: ["cargoCompanyNames", searchStr],
    queryFn: () => searchCargoCompanyNames(searchStr),
    enabled: searchStr.length > 0,
    staleTime: 30 * 1000,
  });

export const useCargoCompanyDetail = (companyGuid: string | null) =>
  useQuery({
    queryKey: ["cargoCompany", companyGuid],
    queryFn: () => getCargoCompany(companyGuid!),
    enabled: !!companyGuid,
    gcTime: 0,
  });
